import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, ErrorState, Input, Loading, Select, Stat, Textarea } from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { OpeningBalanceForm } from '../components/OpeningBalanceForm.jsx';
import { IconPlus, IconWallet } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { money, num, today } from '../lib/format.js';

const COPY = {
  customer: { ...t.party.customers, path: '/customers', endpoint: '/api/customers' },
  supplier: { ...t.party.suppliers, path: '/suppliers', endpoint: '/api/suppliers' },
};

export default function Parties({ type }) {
  const copy = COPY[type];
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [settingOpening, setSettingOpening] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, error, loading, reload } = useApi(copy.endpoint, { q: debouncedSearch, include_inactive: true });

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`${copy.endpoint}/${deleting.id}`);
      toast.success(t.party.deleted(deleting.name));
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const totals = (data || []).reduce(
    (acc, row) => ({
      iqd: acc.iqd + Math.max(0, Number(row.balance_iqd)),
      usd: acc.usd + Math.max(0, Number(row.balance_usd)),
    }),
    { iqd: 0, usd: 0 },
  );

  return (
    <>
      <PageHeader
        title={copy.title}
        subtitle={copy.subtitle}
        actions={
          <button type="button" className="btn btn-sm btn-primary no-print" onClick={() => setEditing({})}>
            <IconPlus /> {t.common.add}
          </button>
        }
      />

      <div className="page">
        <div className="stat-grid">
          <Stat label={copy.totalLabel} value={money(totals.iqd, 'IQD')} hint={money(totals.usd, 'USD')} />
          <Stat label={t.party.accounts} value={num((data || []).length)} />
        </div>

        <Card>
          <Input
            label={t.common.search}
            placeholder={t.party.searchHint}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Card>

        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && data.length === 0 && (
          <Card>
            <Empty
              title={copy.emptyTitle}
              message={copy.emptyHint}
              action={
                <button type="button" className="btn btn-primary" onClick={() => setEditing({})}>
                  <IconPlus /> {copy.addOne}
                </button>
              }
            />
          </Card>
        )}

        {data && data.length > 0 && (
          <div className="record-list">
            {data.map((row) => {
              const owes = Number(row.balance_iqd) > 0 || Number(row.balance_usd) > 0;
              return (
                <div key={row.id} className="record" role="group">
                  <div className="record-top">
                    <Link className="title" to={`${copy.path}/${row.id}`}>
                      {row.name}
                    </Link>
                    <span className={`amount ${owes ? 'neg' : 'muted'}`}>{money(row.balance_iqd, 'IQD')}</span>
                  </div>
                  <div className="record-meta">
                    {row.phone && <span>{row.phone}</span>}
                    {Number(row.balance_usd) !== 0 && <span className="neg">{money(row.balance_usd, 'USD')}</span>}
                    {!row.active && <span className="badge muted">{t.common.inactive}</span>}
                    {!owes && <span className="badge cash">{t.common.settled}</span>}
                  </div>
                  <div className="btn-row no-print" style={{ marginTop: 4 }}>
                    <button type="button" className="btn btn-sm" onClick={() => setPaying(row)}>
                      <IconWallet /> {t.party.payment}
                    </button>
                    <button type="button" className="btn btn-sm" onClick={() => navigate(`${copy.path}/${row.id}`)}>
                      {t.party.statement}
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(row)}>
                      {t.common.edit}
                    </button>
                    {isAdmin && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSettingOpening(row)}>
                        {t.openingBalance.action}
                      </button>
                    )}
                    {isAdmin && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(row)}>
                        {t.common.delete}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {settingOpening && (
        <OpeningBalanceForm
          partyType={type}
          partyId={settingOpening.id}
          partyName={settingOpening.name}
          onClose={() => setSettingOpening(null)}
          onSaved={() => {
            setSettingOpening(null);
            reload();
          }}
        />
      )}

      {editing && (
        <PartyForm
          type={type}
          party={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            toast.success(t.party.saved(saved.name));
            reload();
          }}
        />
      )}

      {paying && (
        <PaymentForm
          type={type}
          party={paying}
          copy={copy}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            toast.success(t.party.paymentSaved);
            reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t.party.deleteTitle}
          message={t.party.deleteMessage(deleting.name)}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </>
  );
}

function PartyForm({ type, party, onClose, onSaved }) {
  const copy = COPY[type];
  const isEdit = Boolean(party.id);
  const [form, setForm] = useState({
    name: party.name || '',
    phone: party.phone || '',
    address: party.address || '',
    notes: party.notes || '',
    currency_pref: party.currency_pref || 'BOTH',
    active: party.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = type === 'supplier' ? form : { ...form, currency_pref: undefined };
      const saved = isEdit
        ? await api.patch(`${copy.endpoint}/${party.id}`, payload)
        : await api.post(copy.endpoint, payload);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? copy.editTitle : copy.newTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !form.name.trim()}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input label={t.common.name} required value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <Input
        label={t.common.phone}
        value={form.phone}
        onChange={(e) => set({ phone: e.target.value })}
        inputMode="tel"
        dir="ltr"
      />
      <Input label={t.common.address} value={form.address} onChange={(e) => set({ address: e.target.value })} />
      {type === 'supplier' && (
        <Select
          label={t.party.invoiceCurrency}
          value={form.currency_pref}
          onChange={(e) => set({ currency_pref: e.target.value })}
          options={[
            { value: 'BOTH', label: t.party.either },
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
          hint={t.party.invoiceCurrencyHint}
        />
      )}
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
      {isEdit && (
        <Select
          label={t.common.status}
          value={form.active ? 'true' : 'false'}
          onChange={(e) => set({ active: e.target.value === 'true' })}
          options={[
            { value: 'true', label: t.common.active },
            { value: 'false', label: t.common.inactive },
          ]}
        />
      )}
    </Modal>
  );
}

export function PaymentForm({ type, party, copy = COPY[type], onClose, onSaved }) {
  const [form, setForm] = useState({
    paid_on: today(),
    amount: '',
    currency: Number(party.balance_usd) > 0 && Number(party.balance_iqd) <= 0 ? 'USD' : 'IQD',
    method: 'cash',
    reference: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const outstanding = form.currency === 'IQD' ? party.balance_iqd : party.balance_usd;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(
        '/api/payments',
        { ...form, party_type: type, party_id: party.id },
        { queueOffline: true, label: `${t.party.payment} — ${party.name}` },
      );
      onSaved(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={copy.paymentTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={busy || !(Number(form.amount) > 0)}
          >
            {busy ? <span className="spinner" /> : t.party.payment}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <p className="small muted mt-0">
        {party.name} — {copy.paymentHint}
      </p>
      <Alert tone="info">
        {t.party.outstandingIn(form.currency === 'IQD' ? t.report.iqd : t.report.usd)}:{' '}
        <strong>{money(outstanding, form.currency)}</strong>
      </Alert>
      <Input
        label={t.common.date}
        required
        type="date"
        value={form.paid_on}
        onChange={(e) => set({ paid_on: e.target.value })}
      />
      <div className="form-grid cols-2">
        <Input
          label={t.common.amount}
          required
          inputMode="decimal"
          value={form.amount}
          onChange={(e) => set({ amount: e.target.value.replace(/[^\d.]/g, '') })}
        />
        <Select
          label={t.common.currency}
          value={form.currency}
          onChange={(e) => set({ currency: e.target.value })}
          options={[
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
        />
      </div>
      <div className="form-grid cols-2">
        <Select
          label={t.common.method}
          value={form.method}
          onChange={(e) => set({ method: e.target.value })}
          options={[
            { value: 'cash', label: t.shared.cash },
            { value: 'transfer', label: 'حوالة مصرفية' },
            { value: 'cheque', label: 'صك' },
            { value: 'other', label: 'أخرى' },
          ]}
        />
        <Input label={t.common.reference} value={form.reference} onChange={(e) => set({ reference: e.target.value })} />
      </div>
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
    </Modal>
  );
}
